import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { BookOpen, Mail, Globe, HelpCircle, Download } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Iter77: Dokumentacija i vodiči — vidljivo unutar Postavki.
 * Sadrži 3 collapsible sekcije: user priručnik, Resend vodič, custom domain vodič.
 * Koristi native <details>/<summary> za sklopive sekcije (bez dodatne biblioteke).
 * Iter79: dodan gumb za skidanje detaljnih korisničkih uputa (PDF sa screenshotima).
 */
export default function DocumentationCard() {
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadManual() {
    try {
      setDownloading(true);
      // Iter104: auth ide preko httpOnly `kif_token` cookie-ja (credentials:include);
      // localStorage token je stari legacy path — uklonjen jer nikad nije setiran.
      const res = await fetch(`${API}/api/settings/user-manual.pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "KIF-portal-upute-za-koristenje.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Upute preuzete");
    } catch (e) {
      toast.error(`Greška pri preuzimanju: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card data-testid="docs-card" className="border-primary/30">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> Dokumentacija i vodiči
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Sva bitna dokumentacija na jednom mjestu — korisnički priručnik, integracija s mail sustavom i migracija na custom domenu.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Iter79: brzo skidanje detaljnih uputa u PDF-u sa screenshotima */}
        <div
          className="rounded-md border border-primary/40 bg-primary/5 p-3 flex items-center justify-between gap-3"
          data-testid="user-manual-download-box"
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-primary flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Korisničke upute — PDF sa print screenovima
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Korak-po-korak vodič za standardnog zaposlenika: prijava, pregled stanja,
              predaja zahtjeva za godišnji odmor i rad na daljinu, praćenje statusa.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleDownloadManual}
            disabled={downloading}
            data-testid="download-user-manual-btn"
            className="shrink-0"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {downloading ? "Priprema…" : "Preuzmi PDF"}
          </Button>
        </div>

        {/* ── A) KORISNIČKI PRIRUČNIK ─────────────────────────────── */}
        <details className="rounded-md border border-border bg-secondary/10 open:bg-white" data-testid="doc-manual">
          <summary className="cursor-pointer px-4 py-3 font-medium text-sm flex items-center gap-2 hover:bg-secondary/20 rounded-md">
            <HelpCircle className="w-4 h-4 text-primary" />
            A) Korisnički priručnik — što KIF portal može
          </summary>
          <div className="px-4 pb-4 pt-2 text-sm space-y-4 leading-relaxed">
            <p className="text-xs italic text-muted-foreground">
              Aplikacija za upravljanje godišnjim odmorima, radom na daljinu, prisutnošću i procesima suglasnosti
              na Kineziološkom fakultetu Sveučilišta u Zagrebu.
            </p>

            <div>
              <div className="font-semibold text-primary">👤 Zaposlenik može:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li><b>Predati zahtjev za godišnji odmor</b> — odabir dana u kalendaru, automatski račun radnih dana i preostalih iz fonda</li>
                <li><b>Predati zahtjev za rad na daljinu (WFH)</b> — s izjavom o sigurnosti + brojem mobitela</li>
                <li><b>Pratiti svoje zahtjeve</b> — status: čeka voditelja / čeka tajništvo / odobreno / vraćeno / odbijeno</li>
                <li><b>Prihvatiti kolektivni godišnji odmor</b> — kad admin aktivira obavezno prihvaćanje</li>
                <li><b>Vidjeti mjesečni raspored prisutnosti</b> — sati, kašnjenja, prekovremeni</li>
                <li><b>RFID skeniranje</b> — kartica na kiosku ili ručna prijava/odjava u aplikaciji</li>
                <li><b>Primati notifikacije</b> — u aplikaciji + e-mail — o novim rješenjima, odbijenim zahtjevima, podsjetnicima</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">👥 Voditelj (šef, prodekan) — sve prethodno PLUS:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li><b>Suglasnosti</b> — pregled zahtjeva svog tima, „Suglasan" / „Vrati na doradu"</li>
                <li><b>Kalendar tima</b> — mjesečni prikaz svih odsutnosti u odjelu</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">🖋️ Tajništvo — sve prethodno PLUS:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li><b>Konačno donošenje rješenja</b> — nakon što voditelj da suglasnost, tajništvo izdaje rješenje (PDF)</li>
                <li><b>Kolektivno izdavanje rješenja</b> — masovno za sve zaposlenike (Bulk Collective)</li>
                <li><b>Izdavanje u ime korisnika</b> — kad zaposlenik ne može sam podnijeti zahtjev</li>
                <li><b>Statistika</b> — pregled iskorištenja fonda po zaposleniku + <b>1-click podsjetnici</b> onima koji nisu predali zahtjev</li>
                <li><b>Postavke aplikacije</b> — kolektivni GO, rezervacija fonda, dodatni praznici</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">🏛️ Dekan:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li>Sve prethodno + <b>konačno potpisivanje rješenja</b></li>
                <li><b>Upload potpisa i pečata</b> koji se automatski utiskuje u PDF-e (Administracija)</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">🛡️ Admin:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li><b>Zaposlenici</b> — dodavanje, uređivanje, brisanje; import iz Excela</li>
                <li><b>Custom uloge</b> — vlastiti nazivi radnih mjesta koji se prikazuju umjesto standardnih</li>
                <li><b>Snapshot baze</b> — snimka trenutnog stanja (fondovi, korisnici, postavke) za brzi vraćanje</li>
                <li><b>Postavke fonda</b> — rezervacija dana za drugi kolektivni GO</li>
                <li><b>Toggle Prisutnost</b> — isključi/uključi rubriku za sve osim admina</li>
                <li><b>Toggle Lokalna prijava</b> — isključi e-mail/lozinku prijavu (svi na M365 SSO, admin ima fail-safe)</li>
                <li><b>Dodavanje/mapiranje RFID kartica</b> — self-enrollment kroz kiosk ili ručno</li>
                <li><b>Prekini SSO sesiju s uređaja</b> — puni MS logout (korisno na tuđim računalima)</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">⚡ Automatske funkcije:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li><b>Auto-checkout 23:59</b> — cronjob svake noći; ako korisnik zaboravi odjaviti, automatski se odjavi točno 8 sati nakon prijave</li>
                <li><b>Prijenos fonda</b> — na 1.1. svake godine, neiskorišteni dani iz prošle godine prenose se do 30.06.</li>
                <li><b>Escalation notifikacije</b> — ako zahtjev čeka voditelja {'>'} 3 dana, obavijesti se tajništvo</li>
                <li><b>E-mail notifikacije</b> — Resend (kad je verificirana domena); u međuvremenu koristi test mode</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">🖥️ Kiosk režim:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li>URL: <code className="text-[11px] bg-secondary px-1 rounded">/kiosk</code> — otvorite u full-screen na uređaju uz RFID čitač</li>
                <li>Zaposlenik prisloni karticu → sustav automatski prijavi/odjavi + prikaže poruku 3 sekunde</li>
                <li>Prvi puta neprepoznata kartica → self-enrollment (user upisuje svoj email da poveže karticu s računom)</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">🔒 Sigurnost:</div>
              <ul className="list-disc ml-5 space-y-0.5 text-xs mt-1">
                <li><b>Dva login flow-a</b>: lokalni (e-mail + lozinka) ili Microsoft 365 SSO</li>
                <li><b>Rate limiting</b> — 5 neuspjelih login pokušaja → 5 min block po IP-u</li>
                <li><b>JWT + refresh token rotacija</b> — access 15 min, refresh 7 dana (u httpOnly cookie)</li>
                <li><b>MFA</b> se koristi kad je konfigurirana u vašem Azure tenantu</li>
              </ul>
            </div>
          </div>
        </details>

        {/* ── B) RESEND VODIČ ─────────────────────────────────────── */}
        <details className="rounded-md border border-border bg-secondary/10 open:bg-white" data-testid="doc-resend">
          <summary className="cursor-pointer px-4 py-3 font-medium text-sm flex items-center gap-2 hover:bg-secondary/20 rounded-md">
            <Mail className="w-4 h-4 text-primary" />
            B) Vodič: Dovršetak integracije s mail sustavom (Resend)
          </summary>
          <div className="px-4 pb-4 pt-2 text-sm space-y-3 leading-relaxed">
            <p className="text-xs italic text-muted-foreground">
              Trenutno je Resend integriran ali <b>ograničen na test mode</b> — može slati e-mailove samo na verificirane adrese
              (obično adresu s kojom je Resend račun kreiran). Za slanje svim zaposlenicima potrebno je verificirati domenu.
            </p>

            <div>
              <div className="font-semibold text-primary">📋 Preduvjeti</div>
              <ul className="list-disc ml-5 text-xs space-y-0.5">
                <li>Aktivan Resend račun (<a href="https://resend.com" target="_blank" rel="noreferrer" className="text-primary underline">resend.com</a>)</li>
                <li>Administratorski pristup DNS zapisima za <code className="bg-secondary px-1 rounded">kif.unizg.hr</code> (u IT sektoru fakulteta)</li>
                <li>API ključ već postavljen u backend/.env kao <code className="bg-secondary px-1 rounded">RESEND_API_KEY</code></li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 1: Dodaj domenu u Resend</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>Prijavi se u <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-primary underline">Resend dashboard → Domains</a></li>
                <li>Klikni <b>„Add Domain"</b></li>
                <li>Upiši <code className="bg-secondary px-1 rounded">kif.unizg.hr</code> i odaberi regiju <b>EU (Frankfurt)</b></li>
                <li>Resend će prikazati popis DNS zapisa koje treba dodati — SPF (TXT), DKIM (TXT), Return-Path (MX ili CNAME)</li>
                <li>Kopiraj ih — u sljedećem koraku šalješ IT-ju</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 2: DNS zapisi (IT sektor fakulteta)</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>Pošalji IT-ju popis zapisa iz Resend dashboarda (obično 3 zapisa: SPF, DKIM, MX)</li>
                <li>Napomena: SPF zapis <b>ne smije prepisati</b> postojeći — treba dopuniti ako već postoji (npr. dodavanjem <code>include:_spf.resend.com</code>)</li>
                <li>IT unosi zapise u DNS zonu <code>kif.unizg.hr</code></li>
                <li>Nakon propagacije (do 24h, obično par minuta) — u Resend dashboardu klikni <b>„Verify DNS Records"</b></li>
                <li>Kad sve postane zeleno ✓ — domena je verificirana</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 3: Ažuriraj from-address u aplikaciji</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>U <code className="bg-secondary px-1 rounded">backend/.env</code>: postavi
                  <code className="block bg-secondary px-2 py-1 rounded mt-1">RESEND_FROM_EMAIL=no-reply@kif.unizg.hr</code>
                  ili druga adresa unutar verificirane domene (npr. <code>obavijesti@kif.unizg.hr</code>)
                </li>
                <li>Napravi <b>Deploy</b> — bez toga backend nastavlja koristiti staru vrijednost</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 4: Test</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>U aplikaciji: <b>Statistika</b> → nađite zaposlenika bez zahtjeva → klik <b>„Pošalji podsjetnik"</b></li>
                <li>Provjerite u njegovom sandučiću stigla je poruka od <code>no-reply@kif.unizg.hr</code></li>
                <li>Alternativa: pošaljite kolektivno rješenje (Tajništvo → „Izdaj kolektivni GO") — svaki zaposlenik dobije PDF u prilogu</li>
              </ol>
            </div>

            <div className="p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
              <b>Napomena:</b> API ključ Resend-a je već u konfiguraciji. Nije ga potrebno mijenjati. Jedino je nužno
              verificirati domenu i postaviti novi <code>RESEND_FROM_EMAIL</code>. Ako će IT trebati puni API pristup
              (troškovi/quota), Resend dashboard je dostupan preko računa koji je kreirao integraciju.
            </div>
          </div>
        </details>

        {/* ── C) CUSTOM DOMAIN VODIČ ──────────────────────────────── */}
        <details className="rounded-md border border-border bg-secondary/10 open:bg-white" data-testid="doc-domain">
          <summary className="cursor-pointer px-4 py-3 font-medium text-sm flex items-center gap-2 hover:bg-secondary/20 rounded-md">
            <Globe className="w-4 h-4 text-primary" />
            C) Vodič: Promjena adrese aplikacije (custom domain) + M365 SSO
          </summary>
          <div className="px-4 pb-4 pt-2 text-sm space-y-3 leading-relaxed">
            <p className="text-xs italic text-muted-foreground">
              Trenutno aplikacija radi na <code className="bg-secondary px-1 rounded">hr-vacation-zt.emergent.host</code>.
              Preporučuje se prebacivanje na vlastitu poddomenu (npr. <code className="bg-secondary px-1 rounded">portal.kif.unizg.hr</code>)
              za profesionalniji dojam i lakše pamćenje.
            </p>

            <div className="p-2 rounded bg-red-50 border border-red-200 text-[11px] text-red-900">
              <b>⚠️ Utjecaj na M365 SSO — VAŽNO:</b>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Azure App Registration je vezan na <b>točan URL</b> (Redirect URI)</li>
                <li>Bez ažuriranja Azure config-a nakon migracije, SSO gumb će vraćati grešku <b>AADSTS50011</b></li>
                <li>Zbog toga <b>prvo</b> pripremite Azure, <b>zatim</b> aktivirajte novu domenu — obrnut redoslijed uzrokuje downtime SSO-a</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">📋 Preduvjeti</div>
              <ul className="list-disc ml-5 text-xs space-y-0.5">
                <li>Odabrani novi domain (npr. <code>portal.kif.unizg.hr</code> ili <code>godisnji.kif.unizg.hr</code>)</li>
                <li>DNS pristup za <code>kif.unizg.hr</code> (IT sektor)</li>
                <li>Admin pristup Azure Entra ID portalu</li>
                <li>Pristup Emergent panelu za deployment (vaš račun)</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 1: Priprema Azure-a (napraviti PRVI)</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>Azure Portal → <b>Microsoft Entra ID</b> → <b>App registrations</b> → vaša aplikacija</li>
                <li>Lijevi izbornik → <b>Authentication</b> → sekcija <b>Single-page application</b></li>
                <li>Klik <b>„Add URI"</b> i dodaj: <code className="block bg-secondary px-2 py-1 rounded mt-1">https://portal.kif.unizg.hr/login</code>
                  <br /><span className="text-muted-foreground">(zamijeni „portal" onime što ste odabrali)</span>
                </li>
                <li><b>Save</b> na dnu stranice</li>
                <li><b>Ne brišite</b> stari <code>hr-vacation-zt.emergent.host/login</code> dok migracija nije završena — potreban je za rollback</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 2: DNS zapis (IT sektor)</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>Pošaljite IT-ju zahtjev za dodavanje CNAME zapisa:
                  <code className="block bg-secondary px-2 py-1 rounded mt-1">portal.kif.unizg.hr → hr-vacation-zt.emergent.host</code>
                </li>
                <li>TTL: 300s ili niži (za brzu propagaciju)</li>
                <li>Nakon što je DNS aktivan (par minuta do sata) — <code>nslookup portal.kif.unizg.hr</code> mora vratiti Emergent IP</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 3: Emergent panel — dodaj custom domain</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>U Emergent chat inputu → izbornik → <b>„Custom Domain"</b> ili u Deploy postavkama</li>
                <li>Upišite <code>portal.kif.unizg.hr</code></li>
                <li>Emergent verificira DNS zapis i automatski izda <b>SSL certifikat</b> (Let's Encrypt)</li>
                <li>Ovo traje 30 sek - 2 min. Nakon toga nova domena je aktivna</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 4: Ažuriraj aplikacijske postavke</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>U <code className="bg-secondary px-1 rounded">frontend/.env</code> promijeni:
                  <code className="block bg-secondary px-2 py-1 rounded mt-1">REACT_APP_BACKEND_URL=https://portal.kif.unizg.hr</code>
                </li>
                <li>U <code className="bg-secondary px-1 rounded">backend/.env</code> ako imate CORS whitelist ili cookie domain postavke — dodajte novu domenu</li>
                <li><b>Deploy</b> — bez ovoga app u novoj domeni bi pokušavao spojiti na staru</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 5: Test</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>Otvori <b>novu</b> domenu → login stranica render-a normalno</li>
                <li>Lokalna prijava (e-mail + lozinka) — mora raditi</li>
                <li>SSO gumb — klik → Microsoft branding → prijava → povratak na <b>novi</b> URL</li>
                <li>Ako SSO vraća AADSTS50011 → Korak 1 nije završen (Redirect URI nije spremljen u Azure)</li>
                <li>Testiraj Odjavu + „Prekini SSO sesiju" — sve funkcije</li>
              </ol>
            </div>

            <div>
              <div className="font-semibold text-primary">✅ Korak 6: Cleanup nakon 1-2 tjedna</div>
              <ol className="list-decimal ml-5 text-xs space-y-1">
                <li>Kad ste sigurni da nova domena radi — u Azure Portal Authentication može se obrisati stari Redirect URI (<code>hr-vacation-zt.emergent.host/login</code>)</li>
                <li>U DNS-u može se maknuti stari CNAME ako je bio pomoćni</li>
                <li>Ažuriraj korisnicima novi URL u mail-u obavijesti / u banner tekstu Login stranice (Postavke → „Poruka na login-u")</li>
              </ol>
            </div>

            <div className="p-2 rounded bg-blue-50 border border-blue-200 text-[11px] text-blue-900">
              <b>💡 Savjet:</b> Radite ovu migraciju <b>izvan radnog vremena</b> (npr. petak navečer) — moguć je kratki
              downtime od 5-10 min tijekom Emergent-DNS propagacije. Obavijestite ključne korisnike unaprijed.
            </div>

            <div className="p-2 rounded bg-green-50 border border-green-200 text-[11px] text-green-900">
              <b>🔄 Rollback plan:</b> Ako nešto pođe po zlu, u Emergent panelu možete odmah maknuti custom domain —
              aplikacija se u sekundama vraća na stari <code>hr-vacation-zt.emergent.host</code>. SSO na starom URL-u
              i dalje radi (nismo brisali stari Azure Redirect URI).
            </div>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}
